// Vegas runner. 4-ball team game settled per-hole.
//
// Each team's hole "number" is the two member scores combined as
// digits, lower digit first. So a team that shoots 3 and 5 gets 35;
// a team that shoots 4 and 4 gets 44. Lower number wins the hole.
// The per-hole differential (|teamA − teamB|) drives the payout:
// winning team gains differential × pointBet from each loser; losing
// team pays the same. Per-player net is symmetric across the two
// team members.
//
// Modifiers:
//   • flipOnBogey — if any player on a team makes bogey or worse
//     (gross score >= par + 1), the team's digits flip so the higher
//     score becomes the leading digit. So a 5 + 7 with the bogey
//     trigger swaps from 57 → 75.
//   • doubleOnBirdie — if any player on a team makes birdie or
//     better (gross <= par − 1), the hole's differential doubles.
//     If BOTH teams have a birdie they each double — they cancel out,
//     so the multiplier just becomes (1 if neither, 2 if one, 4 if
//     both — same as compounding).
//
// Net scoring (scoringMode === "net") subtracts strokes received per
// hole before forming the digit pair. Gross uses raw scores.
//
// Output shape mirrors Hi-Lo so the leaderboard panel can lay it out
// with the same team-vs-team structure.

import { Course, Hole, Player, ScoreMap } from "../types";

function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

export type TeamSetup = "1-2v3-4" | "1-3v2-4" | "1-4v2-3";

export type VegasScoringMode = "net" | "gross";

export interface VegasOptions {
  pointBet?: number;
  scoringMode?: VegasScoringMode;
  teamSetup?: TeamSetup;
  flipOnBogey?: boolean;
  doubleOnBirdie?: boolean;
}

function teamsFromSetup<T>(players: T[], setup?: TeamSetup): [T[], T[]] {
  const [a, b, c, d] = players;
  if (setup === "1-3v2-4") return [[a, c], [b, d]];
  if (setup === "1-4v2-3") return [[a, d], [b, c]];
  return [[a, b], [c, d]];
}

export interface VegasHoleDetail {
  // 1-based hole number.
  hole: number;
  // The two team "numbers" combined from digits (e.g. 35).
  teamANumber: number;
  teamBNumber: number;
  // Per-hole differential in this team's favour (positive = team gained).
  // teamADelta = teamBNumber − teamANumber after multiplier applied.
  teamADelta: number;
  // True when this team's row got a doubling birdie this hole.
  // Informational — same per hole for both teams since the multiplier
  // applies before the differential is signed.
  multiplier: number;
}

export interface VegasPayout {
  // Both teammates share the same numbers — both also carry the same
  // per-team totals so the leaderboard can render per-row without
  // having to look up the partner.
  team: "teamA" | "teamB";
  // Sum of signed deltas for this player's team across the round.
  // Positive = team is ahead on aggregate differential.
  teamDelta: number;
  // Net cash for this player = teamDelta × pointBet (since each player
  // shares the team's diff and the bet rate applies once). Equal to
  // teammate's; opposite sign to opposing team.
  total: number;
  // Per-hole detail rows (this player's team's view), populated for
  // every settled hole. Holes without complete scores from both teams
  // are skipped.
  details: VegasHoleDetail[];
}

export interface VegasResult {
  payouts: Record<string, VegasPayout>;
  hasAnyBet: boolean;
  teamDeltaTotal: { teamA: number; teamB: number };
  scoringMode: VegasScoringMode;
}

export interface VegasInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  options: VegasOptions;
}

function emptyPayout(team: "teamA" | "teamB"): VegasPayout {
  return { team, teamDelta: 0, total: 0, details: [] };
}

// Form the team "number" from a pair of scores. Lower digit always
// goes first (e.g. 5 and 3 → "35" → 35). Scores beyond single digits
// (rare — 10+) are concatenated as decimal strings, so 10 + 4 → "410"
// would mean 4 first and 10 trailing, which we serialize as 410 by
// string concat. This is the convention the user spec implies.
function vegasNumberFromPair(a: number, b: number, flip: boolean): number {
  const low = Math.min(a, b);
  const high = Math.max(a, b);
  // Penalty flip: the higher digit becomes the leading digit when any
  // team member made bogey or worse on the hole.
  const leading = flip ? high : low;
  const trailing = flip ? low : high;
  return parseInt(`${leading}${trailing}`, 10);
}

export function runVegas(input: VegasInput): VegasResult {
  const { course, players, scores, options } = input;
  const pointBet = Number(options.pointBet) || 0;
  const scoringMode: VegasScoringMode =
    options.scoringMode === "net" ? "net" : "gross";
  const flipOnBogey = options.flipOnBogey !== false;
  const doubleOnBirdie = !!options.doubleOnBirdie;

  // Defensive — Vegas only makes sense at 4 players.
  if (players.length !== 4) {
    const payouts: Record<string, VegasPayout> = {};
    for (const p of players) payouts[p.id] = emptyPayout("teamA");
    return {
      payouts,
      hasAnyBet: false,
      teamDeltaTotal: { teamA: 0, teamB: 0 },
      scoringMode,
    };
  }

  const [teamA, teamB] = teamsFromSetup(players, options.teamSetup);

  // Course handicap cache for net scoring.
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];
  const courseHcpById: Record<string, number> = {};
  for (const p of players) {
    const tee = tees.find((t) => t.id === p.tee);
    courseHcpById[p.id] = courseHandicap(
      p.handicap,
      tee?.slope,
      tee?.rating,
      totalPar,
    );
  }

  const scoreFor = (
    playerId: string,
    hole: Hole,
  ): { gross: number; net: number } | null => {
    const sc = (scores[playerId] || {})[hole.number];
    if (!sc || typeof sc.gross !== "number") return null;
    const net = sc.gross - strokesOnHole(courseHcpById[playerId], hole);
    return { gross: sc.gross, net };
  };

  const payouts: Record<string, VegasPayout> = {};
  for (const p of teamA) payouts[p.id] = emptyPayout("teamA");
  for (const p of teamB) payouts[p.id] = emptyPayout("teamB");

  let teamARunning = 0;
  let teamBRunning = 0;

  for (const hole of course.holes) {
    const aScores: Array<{ gross: number; net: number }> = [];
    const bScores: Array<{ gross: number; net: number }> = [];
    let complete = true;
    for (const p of teamA) {
      const s = scoreFor(p.id, hole);
      if (s == null) {
        complete = false;
        break;
      }
      aScores.push(s);
    }
    if (!complete) continue;
    for (const p of teamB) {
      const s = scoreFor(p.id, hole);
      if (s == null) {
        complete = false;
        break;
      }
      bScores.push(s);
    }
    if (!complete) continue;

    // Pick the relevant score (gross vs net) per the option, then
    // compute each team's Vegas number. flipOnBogey checks the GROSS
    // scores (a bogey is a gross-vs-par concept regardless of the
    // settlement basis), but the digits used are the scoringMode
    // value so net penalties don't double up.
    const aValues = aScores.map((s) =>
      scoringMode === "net" ? s.net : s.gross,
    );
    const bValues = bScores.map((s) =>
      scoringMode === "net" ? s.net : s.gross,
    );
    const aFlip =
      flipOnBogey && aScores.some((s) => s.gross >= hole.par + 1);
    const bFlip =
      flipOnBogey && bScores.some((s) => s.gross >= hole.par + 1);
    const aNumber = vegasNumberFromPair(aValues[0], aValues[1], aFlip);
    const bNumber = vegasNumberFromPair(bValues[0], bValues[1], bFlip);

    // Doubling: each team with at least one birdie or better doubles
    // its multiplier. Stacked when both sides hit (extremely rare).
    let multiplier = 1;
    if (doubleOnBirdie) {
      if (aScores.some((s) => s.gross <= hole.par - 1)) multiplier *= 2;
      if (bScores.some((s) => s.gross <= hole.par - 1)) multiplier *= 2;
    }

    // Lower number wins. Differential is positive for team A's row
    // when A wins, negative when A loses. Multiplier scales it.
    const rawDiff = bNumber - aNumber; // positive = A ahead
    const teamADelta = rawDiff * multiplier;

    teamARunning += teamADelta;
    teamBRunning -= teamADelta;

    for (const p of teamA) {
      payouts[p.id].details.push({
        hole: hole.number,
        teamANumber: aNumber,
        teamBNumber: bNumber,
        teamADelta,
        multiplier,
      });
    }
    for (const p of teamB) {
      payouts[p.id].details.push({
        hole: hole.number,
        teamANumber: aNumber,
        teamBNumber: bNumber,
        teamADelta,
        multiplier,
      });
    }
  }

  for (const p of teamA) {
    const pay = payouts[p.id];
    pay.teamDelta = teamARunning;
    pay.total = teamARunning * pointBet;
  }
  for (const p of teamB) {
    const pay = payouts[p.id];
    pay.teamDelta = teamBRunning;
    pay.total = teamBRunning * pointBet;
  }

  return {
    payouts,
    hasAnyBet: pointBet > 0,
    teamDeltaTotal: { teamA: teamARunning, teamB: teamBRunning },
    scoringMode,
  };
}
